import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  IconButton,
  Stack,
  Paper,
  Chip,
  Divider,
  Tooltip,
  Grid,
  LinearProgress,
  useTheme,
  useMediaQuery,
  MenuItem,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import ApiConfig from "../../Config";
import homeService from "../../repository/HomeRepository";

const BannerManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const bannerTypeOptions = [
    {
      label: "App Banner",
      value: "app",
    },
    {
      label: "Web Banner",
      value: "web",
    },
    {
      label: "WhatsApp Banner",
      value: "whatsApp",
    },
  ];

  const [newBanner, setNewBanner] = useState({
    bannerName: "",
    description: "",
    imageURL: null,
    bannerType: "",
  });
  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    cardAlt: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    danger: isDark ? "#D99494" : "#B54545",
  };

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.14)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "9px",
      backgroundColor: colors.cardAlt,
      color: colors.text,

      "& fieldset": {
        borderColor: colors.border,
      },

      "&:hover fieldset": {
        borderColor: colors.accent,
      },

      "&.Mui-focused fieldset": {
        borderColor: colors.accent,
      },
    },

    "& .MuiInputLabel-root": {
      color: colors.secondary,
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.accent,
    },

    "& .MuiInputBase-input::placeholder": {
      color: colors.secondary,
    },
  };

  const fetchBanners = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const res = await homeService.getBaner();
      const data = Array.isArray(res.bannerData) ? res.bannerData : [];

      setBanners(data);
    } catch (err) {
      console.error("Banner fetch error:", err);

      setError(err?.response?.data?.message || "Failed to fetch banners");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess("");
    }, 3000);

    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [error]);

  const handleVisibilityToggle = async (bannerId, isVisible) => {
    try {
      setTogglingId(bannerId);
      setError("");

      await homeService.setVisibilityBanner(!isVisible, bannerId);

      setBanners((prev) =>
        prev.map((banner) =>
          banner.bannerId === bannerId
            ? {
                ...banner,
                isVisible: !isVisible,
              }
            : banner,
        ),
      );

      setSuccess(!isVisible ? "Banner is now visible" : "Banner is now hidden");
    } catch (err) {
      console.error("Visibility toggle failed:", err);

      setError(
        err?.response?.data?.message || "Failed to update banner visibility",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const openDeleteDialog = (banner) => {
    setBannerToDelete(banner);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (deletingId) return;

    setDeleteDialog(false);
    setBannerToDelete(null);
  };

  const handleDeleteBanner = async () => {
    if (!bannerToDelete?.bannerId) return;

    try {
      setDeletingId(bannerToDelete.bannerId);
      setError("");

      await homeService.deleteBanner(bannerToDelete.bannerId);

      setBanners((prev) =>
        prev.filter((banner) => banner.bannerId !== bannerToDelete.bannerId),
      );

      setDeleteDialog(false);
      setBannerToDelete(null);

      setSuccess("Banner deleted successfully");
    } catch (err) {
      console.error("Banner deletion failed:", err);

      setError(err?.response?.data?.message || "Failed to delete banner");
    } finally {
      setDeletingId(null);
    }
  };

  const clearImagePreview = useCallback(() => {
    setImagePreview((currentPreview) => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview);
      }

      return "";
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "imageURL") {
      const file = files?.[0];

      if (!file) return;

      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");

        e.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5 MB");

        e.target.value = "";
        return;
      }

      clearImagePreview();

      const previewUrl = URL.createObjectURL(file);

      setImagePreview(previewUrl);

      setNewBanner((prev) => ({
        ...prev,
        imageURL: file,
      }));

      return;
    }

    setNewBanner((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = useCallback(() => {
    setImagePreview((currentPreview) => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview);
      }

      return "";
    });

    setNewBanner({
      bannerName: "",
      description: "",
      imageURL: null,
      bannerType: "",
    });

    const input = document.getElementById("banner-image-upload");

    if (input) {
      input.value = "";
    }
  }, []);

  const handleCloseDialog = () => {
    if (uploading) return;

    setOpenDialog(false);
    resetForm();
  };

  const handleAddBanner = async () => {
    if (!newBanner.bannerName.trim()) {
      setError("Banner name is required");
      return;
    }

    if (!newBanner.bannerType.trim()) {
      setError("Banner type is required");
      return;
    }

    if (!newBanner.imageURL) {
      setError("Please select a banner image");
      return;
    }

    try {
      setUploading(true);
      setError("");

      const formData = new FormData();

      formData.append("bannerName", newBanner.bannerName.trim());

      formData.append("description", newBanner.description.trim());

      formData.append("bannerType", newBanner.bannerType.trim());

      formData.append("imageURL", newBanner.imageURL);

      await homeService.addBanner(formData);

      setOpenDialog(false);
      resetForm();
      await fetchBanners(true);
      setSuccess("Banner added successfully");
    } catch (err) {
      console.error("Banner upload failed:", err);

      setError(err?.response?.data?.message || "Failed to upload banner");
    } finally {
      setUploading(false);
    }
  };

  const visibleCount = banners.filter((banner) =>
    Boolean(banner.isVisible),
  ).length;

  const hiddenCount = banners.length - visibleCount;

  const BannerCard = ({ banner }) => {
    const isToggling = togglingId === banner.bannerId;

    return (
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          overflow: "hidden",
          height: "100%",
          transition: "transform .2s ease, box-shadow .2s ease",

          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: isDark
              ? "0 14px 40px rgba(0,0,0,.22)"
              : "0 14px 40px rgba(50,40,30,.09)",
          },
        }}
      >
        {/* IMAGE */}
        <Box
          sx={{
            position: "relative",
            height: {
              xs: 180,
              sm: 190,
              md: 200,
            },
            backgroundColor: colors.cardAlt,
            overflow: "hidden",
          }}
        >
          {banner.bannerIcon ? (
            <Box
              component="img"
              src={banner.bannerIcon}
              alt={banner.bannerName || "Banner"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.secondary,
              }}
            >
              <ImageOutlinedIcon sx={{ fontSize: 42 }} />
            </Box>
          )}

          {/* IMAGE OVERLAY */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top,rgba(0,0,0,.55),transparent 55%)",
            }}
          />

          {/* VISIBILITY CHIP */}
          <Chip
            icon={
              banner.isVisible ? (
                <VisibilityOutlinedIcon
                  sx={{
                    fontSize: "14px !important",
                  }}
                />
              ) : (
                <VisibilityOffOutlinedIcon
                  sx={{
                    fontSize: "14px !important",
                  }}
                />
              )
            }
            label={banner.isVisible ? "Visible" : "Hidden"}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              height: 27,
              borderRadius: "7px",
              fontSize: 10,
              fontWeight: 700,
              color: "#FFFFFF",
              backgroundColor: banner.isVisible
                ? "rgba(61,128,84,.88)"
                : "rgba(60,56,52,.88)",
              backdropFilter: "blur(8px)",

              "& .MuiChip-icon": {
                color: "#FFFFFF",
              },
            }}
          />

          {/* DELETE */}
          <IconButton
            onClick={() => openDeleteDialog(banner)}
            disabled={deletingId === banner.bannerId}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 34,
              height: 34,
              color: "#FFFFFF",
              backgroundColor: "rgba(0,0,0,.35)",
              backdropFilter: "blur(8px)",

              "&:hover": {
                backgroundColor: "rgba(181,69,69,.9)",
              },
            }}
          >
            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {/* NAME */}
          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              left: 14,
              right: 14,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 650,
                color: "#FFFFFF",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {banner.bannerName || "Untitled Banner"}
            </Typography>
          </Box>
        </Box>

        {/* CONTENT */}
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.2,
            }}
          >
            <Chip
              label={banner.bannerType || "General"}
              size="small"
              sx={{
                height: 24,
                borderRadius: "6px",
                fontSize: 10,
                fontWeight: 650,
                color: colors.accent,
                backgroundColor: colors.accentSoft,
                border: `1px solid ${colors.border}`,
              }}
            />

            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              Banner
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 12,
              lineHeight: 1.6,
              color: colors.secondary,
              minHeight: 38,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {banner.description || "No description available."}
          </Typography>

          <Divider
            sx={{
              my: 1.5,
              borderColor: colors.border,
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  color: colors.secondary,
                }}
              >
                STATUS
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 650,
                  color: banner.isVisible ? colors.success : colors.secondary,
                  mt: 0.25,
                }}
              >
                {banner.isVisible ? "Live on website" : "Currently hidden"}
              </Typography>
            </Box>

            <Tooltip title={banner.isVisible ? "Hide banner" : "Show banner"}>
              <span>
                <Switch
                  checked={Boolean(banner.isVisible)}
                  onChange={() =>
                    handleVisibilityToggle(banner.bannerId, banner.isVisible)
                  }
                  disabled={isToggling}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: colors.success,
                    },

                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.success,
                    },

                    "& .MuiSwitch-track": {
                      backgroundColor: isDark ? "#4B4742" : "#C9C2B9",
                    },
                  }}
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          backgroundColor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress
          size={28}
          sx={{
            color: colors.accent,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.background,
        color: colors.text,
        p: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        {/* BACK */}
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackRoundedIcon />}
          sx={{
            mb: 2,
            px: 1.5,
            height: 36,
            borderRadius: "8px",
            color: colors.secondary,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.card,
            textTransform: "none",
            fontSize: 11.5,
            fontWeight: 600,
            boxShadow: "none",

            "&:hover": {
              color: colors.accent,
              borderColor: colors.accent,
              backgroundColor: colors.accentSoft,
              boxShadow: "none",
            },
          }}
        >
          Back to Home Management
        </Button>

        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
              }}
            >
              <CollectionsOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: {
                    xs: 23,
                    sm: 28,
                    md: 30,
                  },
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: colors.text,
                }}
              >
                Banner Management
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: 11.5,
                    sm: 13,
                  },
                  color: colors.secondary,
                  mt: 0.5,
                }}
              >
                Manage homepage banners and visibility
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setOpenDialog(true)}
            fullWidth={isMobile}
            sx={{
              height: 42,
              px: 2,
              borderRadius: "9px",
              backgroundColor: colors.text,
              color: isDark ? "#151412" : "#FFFFFF",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 650,
              boxShadow: "none",

              "&:hover": {
                backgroundColor: colors.accent,
                boxShadow: "none",
              },
            }}
          >
            Add Banner
          </Button>
        </Box>

        {/* STATS */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(3,1fr)",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              TOTAL BANNERS
            </Typography>

            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 650,
                color: colors.text,
                mt: 0.4,
              }}
            >
              {banners.length}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              VISIBLE
            </Typography>

            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 650,
                color: colors.success,
                mt: 0.4,
              }}
            >
              {visibleCount}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
              display: {
                xs: "none",
                sm: "block",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              HIDDEN
            </Typography>

            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 650,
                color: colors.secondary,
                mt: 0.4,
              }}
            >
              {hiddenCount}
            </Typography>
          </Paper>
        </Box>

        {/* REFRESH */}
        {refreshing && (
          <LinearProgress
            sx={{
              mb: 2,
              height: 2,
              backgroundColor: colors.accentSoft,

              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.accent,
              },
            }}
          />
        )}

        {/* BANNERS */}
        {banners.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              py: 9,
              px: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: "15px",
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                mb: 2,
              }}
            >
              <CollectionsOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>

            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 20,
                fontWeight: 600,
                color: colors.text,
              }}
            >
              No banners yet
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.secondary,
                mt: 0.6,
                mb: 2.5,
              }}
            >
              Create your first homepage banner to get started.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                borderRadius: "9px",
                borderColor: colors.border,
                color: colors.accent,
                textTransform: "none",
                fontSize: 12,

                "&:hover": {
                  borderColor: colors.accent,
                  backgroundColor: colors.accentSoft,
                },
              }}
            >
              Create Banner
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {banners.map((banner) => (
              <Grid
                item
                xs={12}
                sm={6}
                lg={4}
                key={banner._id || banner.bannerId}
              >
                <BannerCard banner={banner} />
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          transitionDuration={0}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : "16px",
              backgroundColor: colors.card,
              backgroundImage: "none",
              border: `1px solid ${colors.border}`,
              boxShadow: "0 25px 70px rgba(0,0,0,.2)",
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 2.5,
              pb: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: 22,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  Add New Banner
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.secondary,
                    mt: 0.4,
                  }}
                >
                  Create a new homepage promotional banner
                </Typography>
              </Box>

              <IconButton
                onClick={handleCloseDialog}
                disabled={uploading}
                sx={{
                  color: colors.secondary,

                  "&:hover": {
                    color: colors.accent,
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              {/* NAME */}
              <TextField
                label="Banner Name"
                name="bannerName"
                fullWidth
                required
                value={newBanner.bannerName}
                onChange={handleInputChange}
                sx={fieldSx}
              />

              {/* DESCRIPTION */}
              <TextField
                label="Description"
                name="description"
                fullWidth
                multiline
                minRows={3}
                value={newBanner.description}
                onChange={handleInputChange}
                sx={fieldSx}
              />

              {/* TYPE */}
              <TextField
                select
                label="Banner Type"
                name="bannerType"
                fullWidth
                required
                value={newBanner.bannerType}
                onChange={handleInputChange}
                sx={fieldSx}
                SelectProps={{
                  displayEmpty: true,
                }}
              >
                <MenuItem value="" disabled>
                  Select Banner Type
                </MenuItem>

                {bannerTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* IMAGE */}
              <Box>
                <input
                  id="banner-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  name="imageURL"
                  onChange={handleInputChange}
                />

                <Box
                  component="label"
                  htmlFor="banner-image-upload"
                  sx={{
                    display: "block",
                    border: `1px dashed ${
                      newBanner.imageURL ? colors.accent : colors.border
                    }`,
                    borderRadius: "11px",
                    p: 2,
                    cursor: "pointer",
                    backgroundColor: colors.cardAlt,
                    transition: "all .2s ease",

                    "&:hover": {
                      borderColor: colors.accent,
                      backgroundColor: colors.accentSoft,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: colors.accentSoft,
                        color: colors.accent,
                      }}
                    >
                      <CloudUploadOutlinedIcon />
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 650,
                          color: colors.text,
                        }}
                      >
                        {newBanner.imageURL
                          ? "Change banner image"
                          : "Upload banner image"}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 10.5,
                          color: colors.secondary,
                          mt: 0.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {newBanner.imageURL
                          ? newBanner.imageURL.name
                          : "PNG, JPG or WEBP • Max 5 MB"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* PREVIEW */}
                {imagePreview && (
                  <Box
                    sx={{
                      mt: 1.5,
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.cardAlt,
                    }}
                  >
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Banner preview"
                      sx={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* INFO */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "9px",
                  backgroundColor: colors.accentSoft,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <CheckCircleOutlineRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: colors.accent,
                    mt: 0.1,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: colors.secondary,
                    lineHeight: 1.5,
                  }}
                >
                  Recommended: Use a high-quality landscape image suitable for
                  the homepage banner.
                </Typography>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 1,
              gap: 1,
            }}
          >
            <Button
              onClick={handleCloseDialog}
              disabled={uploading}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "9px",
                color: colors.secondary,
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleAddBanner}
              variant="contained"
              disabled={uploading}
              startIcon={
                uploading ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: "inherit",
                    }}
                  />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              sx={{
                height: 40,
                px: 2.5,
                borderRadius: "9px",
                backgroundColor: colors.text,
                color: isDark ? "#151412" : "#FFFFFF",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 650,
                boxShadow: "none",

                "&:hover": {
                  backgroundColor: colors.accent,
                  boxShadow: "none",
                },
              }}
            >
              {uploading ? "Uploading..." : "Upload Banner"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ----------------------------------------------------------------- */}
        {/* DELETE DIALOG */}
        {/* ----------------------------------------------------------------- */}

        <Dialog
          open={deleteDialog}
          onClose={closeDeleteDialog}
          maxWidth="xs"
          fullWidth
          transitionDuration={0}
          PaperProps={{
            sx: {
              borderRadius: "14px",
              backgroundColor: colors.card,
              backgroundImage: "none",
              border: `1px solid ${colors.border}`,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 20,
                fontWeight: 600,
                color: colors.text,
              }}
            >
              Delete Banner?
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Typography
              sx={{
                fontSize: 12.5,
                color: colors.secondary,
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to delete{" "}
              <strong
                style={{
                  color: colors.text,
                }}
              >
                {bannerToDelete?.bannerName}
              </strong>
              ? This action cannot be undone.
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2,
              gap: 1,
            }}
          >
            <Button
              onClick={closeDeleteDialog}
              disabled={Boolean(deletingId)}
              sx={{
                borderRadius: "8px",
                color: colors.secondary,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleDeleteBanner}
              disabled={Boolean(deletingId)}
              startIcon={
                deletingId ? (
                  <CircularProgress
                    size={15}
                    sx={{
                      color: "inherit",
                    }}
                  />
                ) : (
                  <DeleteOutlineRoundedIcon />
                )
              }
              sx={{
                borderRadius: "8px",
                backgroundColor: colors.danger,
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: 12,

                "&:hover": {
                  backgroundColor: colors.danger,
                  opacity: 0.88,
                },
              }}
            >
              {deletingId ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {error && (
          <Box
            sx={{
              position: "fixed",
              right: {
                xs: 12,
                sm: 24,
              },
              bottom: {
                xs: 12,
                sm: 24,
              },
              zIndex: 9999,
              maxWidth: {
                xs: "calc(100vw - 24px)",
                sm: 420,
              },
            }}
          >
            <Alert
              severity="error"
              variant="filled"
              onClose={() => setError("")}
              sx={{
                borderRadius: "10px",
                boxShadow: "0 10px 30px rgba(0,0,0,.18)",
              }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {success && (
          <Box
            sx={{
              position: "fixed",
              right: {
                xs: 12,
                sm: 24,
              },
              bottom: {
                xs: 12,
                sm: 24,
              },
              zIndex: 9999,
              maxWidth: {
                xs: "calc(100vw - 24px)",
                sm: 420,
              },
            }}
          >
            <Alert
              severity="success"
              variant="filled"
              onClose={() => setSuccess("")}
              sx={{
                borderRadius: "10px",
                boxShadow: "0 10px 30px rgba(0,0,0,.18)",
              }}
            >
              {success}
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BannerManagement;
